import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useHistory, useLocation } from "react-router-dom";
import classNames from "classnames";
import dompurify from "isomorphic-dompurify";
import { getAgeVerificationUuid, prepareAddressForAv, removeAgeVerificationUuid, removeCookie, setAgeVerificationUuid, translateBackendErrorCode, } from "@jti/fe-common";
import { useAvpActions } from "@jti/rest-api";
import { dispatchTrackingEvent, TrackingEvents } from "@jti/tracker";
import { Button } from "../../components/Button";
import { useFormContext } from "../../components/Form";
import { Loading } from "../../components/Loading";
import { Txt } from "../../components/Txt";
import { useConfig } from "../../hooks/useConfig";
import { useQueryStringParams } from "../../hooks/useQueryStringParams";
import { useSessionAsyncAv } from "../../hooks/useSessionAsyncAv";
import passportImage from "../../images/passport.png";
import { useTranslations } from "../../providers/useTranslations";
import { AsyncValidationPopup } from "./AsyncValidationPopup/AsyncValidationPopup";
import { FieldsArray } from "./FieldsArray/FieldsArray";
import { useAvMethods } from "./hooks/useAvMethods";
import { MethodSelection } from "./MethodSelection/MethodSelection";
import { AvMethodsComponents, SyncAvWidgets } from "./SyncAvWidgets/SyncAvWidgets";
import styles from "./ValidationMethods.module.scss";
const { sanitize } = dompurify;
const formPrefix = "avpValues";
const adultoCookieKey = "adultocz_local";
const VERIFF_AV = "veriff_av";
const uuidCookieTtl = 30 * 60; // 30 minutes - the same as the session timeout for BE for AV provider resposne - possible to change in the future if needed based on Market feedback
export const ValidationMethods = memo(({ selectedMethod, selectMethod, isOptional, isPending, selectedMethodImage, userData = {}, showImage = true, onSuccess, onFail, buttonClassName, buttonLabel, children, isDisabled, baseUrl = window.location.href, onLoadDataFromCache, onSaveDataToCache, }) => {
    const config = useConfig();
    const { __ } = useTranslations();
    const { search } = useLocation();
    const history = useHistory();
    const querySearch = useQueryStringParams();
    const { getAsyncAvData, saveAsyncAvData, clearAsyncAvData } = useSessionAsyncAv();
    const [isAsyncPendingPopupOpened, setAsyncPendingPopupOpened] = useState(false);
    const [isValidationError, setIsValidationError] = useState(false);
    const [savedAsyncAvData, setSavedAsyncAvData] = useState(null);
    const [isButtonDisabledForWidgetVerification, setIsButtonDisabledForWidgetVerification] = useState(true);
    const form = useFormContext();
    const { verify, initAsyncVerification, asyncVerificationPending, asyncVerificationData, getAsyncMethodConfig, loadAsyncAgeVerificationScript, isVerifing, isAsyncVerificationFailed, } = useAvpActions();
    // @TODO: Fix the passportImage fallback issue where it's not returning the expected path to the image file as a string.
    const fallbackAgeVerificationImage = config.AV_PHOTO || passportImage;
    const imagePath = selectedMethodImage || fallbackAgeVerificationImage || "";
    const { method, onSelectMethod, availableMethods, isAvailableMethodsLoading } = useAvMethods({
        selectedMethod,
        selectMethod,
    });
    useEffect(() => {
        const isSelectedMethodWidget = (method) => Object.hasOwn(AvMethodsComponents, method);
        if (selectedMethod) {
            setIsButtonDisabledForWidgetVerification(isSelectedMethodWidget(selectedMethod));
        }
    }, [selectedMethod]);
    useEffect(() => {
        if (asyncVerificationData) {
            const { status } = asyncVerificationData.data?.response_body || {};
            form.setValue("avpValues.request_uuid", asyncVerificationData.uuid);
            setAgeVerificationUuid(asyncVerificationData.uuid, uuidCookieTtl);
            if (status === "success") {
                onVeriffResponse();
            }
            else {
                const workflowName = getAsyncMethodConfig(selectedMethod)?.workflowName;
                form.setValue("avpValues.workflowName", workflowName);
                loadAsyncAgeVerificationScript(selectedMethod, asyncVerificationData, {
                    token: asyncVerificationData.data?.token,
                    onSuccess: () => {
                        asyncAgeVerify({
                            ageVerificationMethodCode: selectedMethod,
                            dateOfBirth: userData?.dateOfBirth,
                            email: userData.email,
                            firstName: userData?.firstname,
                            lastName: userData?.lastname,
                            request_uuid: asyncVerificationData.uuid,
                            workflowName,
                            sessionUrl: "",
                        });
                    },
                });
            }
        }
    }, [asyncVerificationData]);
    useEffect(() => {
        const asyncAvData = getAsyncAvData();
        if (querySearch.redirectedFromAsyncMethod &&
            !Array.isArray(querySearch.redirectedFromAsyncMethod) &&
            asyncAvData) {
            selectMethod(querySearch.redirectedFromAsyncMethod);
            setSavedAsyncAvData(asyncAvData);
            asyncAgeVerify(asyncAvData);
            setAsyncPendingPopupOpened(true);
            clearAsyncAvData();
            onLoadDataFromCache?.(asyncAvData);
            // Clear url after redirection
            history.replace({
                search: "",
            });
        }
    }, [search, selectMethod]);
    useEffect(() => {
        dispatchTrackingEvent(TrackingEvents.TrackingHardAgeVerificationStart);
    }, []);
    const clearWidgetAvData = useCallback(() => {
        removeCookie(adultoCookieKey);
    }, []);
    useEffect(() => {
        return () => {
            clearWidgetAvData();
        };
    }, [clearWidgetAvData]);
    const asyncAgeVerify = async ({ ageVerificationMethodCode, dateOfBirth = "", email, firstName = "", lastName = "", request_uuid, workflowName, }) => {
        try {
            const { uuid: avpUuid, is_verified, status_from_provider, } = await verify({
                user_email: email,
                country_code: config.ISO_COUNTRY_CODE,
                method_code: ageVerificationMethodCode,
                fields: {
                    firstname: firstName,
                    lastname: lastName,
                    date_of_birth: dateOfBirth,
                    request_uuid,
                    workflow_name: workflowName,
                },
            });
            if (status_from_provider === "pending") {
                setAsyncPendingPopupOpened(true);
                return;
            }
            if (status_from_provider === "failed") {
                onAgeVerificationFail();
                return;
            }
            if (status_from_provider === "success") {
                setAsyncPendingPopupOpened(false);
            }
            onAgeVerification(avpUuid, is_verified);
        }
        catch (error) {
            onAgeVerificationFail();
        }
    };
    const verifyAge = async (formValues, verificationMethodCode) => {
        try {
            const { firstname, lastname, dateOfBirth, phone, email, addresses } = userData;
            const { uuid: avpUuid, is_verified } = await verify({
                user_email: email,
                country_code: config.ISO_COUNTRY_CODE,
                method_code: verificationMethodCode,
                fields: {
                    ...formValues.avpValues,
                    ...(userData?.additionalData || {}),
                    firstname,
                    lastname,
                    date_of_birth: dateOfBirth,
                    phone,
                    ...prepareAddressForAv(addresses),
                },
            });
            if (is_verified) {
                dispatchTrackingEvent(TrackingEvents.TrackingHardAgeVerificationSuccess, {
                    hav_method: verificationMethodCode,
                });
            }
            onAgeVerification(avpUuid, is_verified);
        }
        catch (error) {
            const errorMessage = translateBackendErrorCode(error?.error_code, error?.message);
            onAgeVerificationFail(errorMessage);
        }
    };
    const isFormFilled = () => Object.values(form.getValues()?.avpValues || {}).some(Boolean);
    const onAgeVerification = async (avpUuid, is_verified) => {
        if (avpUuid && is_verified) {
            removeAgeVerificationUuid();
            clearWidgetAvData();
            onSuccess?.(avpUuid);
        }
        else if ((isOptional && isFormFilled()) || (!isOptional && !is_verified)) {
            onAgeVerificationFail();
        }
    };
    const onAgeVerificationFail = (message) => {
        removeAgeVerificationUuid();
        setIsValidationError(true);
        toast.error(message || __("auth.age_verification.result.message_fail"));
        onFail?.(message);
    };
    const onVeriffResponse = () => {
        if (asyncVerificationData) {
            const { status, verification } = asyncVerificationData.data?.response_body || {};
            form.setValue("ageVerificationMethodCode", selectedMethod);
            if (status === "success" && verification?.url) {
                saveAsyncAvData({
                    ageVerificationMethodCode: selectedMethod,
                    dateOfBirth: userData.dateOfBirth,
                    email: userData.email,
                    firstName: userData.firstname,
                    lastName: userData.lastname,
                    request_uuid: asyncVerificationData.uuid,
                    sessionUrl: verification.url,
                });
                onSaveDataToCache?.();
                window.location.href = verification.url;
            }
        }
    };
    const initAsyncMethod = (selectedMethod) => {
        return initAsyncVerification({
            methodCode: selectedMethod,
            userEmail: userData?.email || "",
            fields: {
                callback: `${baseUrl}?redirectedFromAsyncMethod=${selectedMethod}`,
                firstName: userData?.firstname || "",
                lastName: userData?.lastname || "",
                dateOfBirth: userData?.dateOfBirth,
                workflowName: getAsyncMethodConfig(selectedMethod)?.workflowName,
            },
        });
    };
    // additional verification for async methods - motivated by veriff issue with long response time
    const handleCheckPrevAsyncVerification = async () => {
        const uuidFromCookie = getAgeVerificationUuid();
        if (!uuidFromCookie) {
            return false;
        }
        try {
            const { is_verified, status_from_provider } = await verify({
                user_email: userData.email,
                country_code: config.ISO_COUNTRY_CODE,
                method_code: selectedMethod,
                fields: {
                    firstname: userData.firstname,
                    lastname: userData.lastname,
                    date_of_birth: userData.dateOfBirth,
                    request_uuid: uuidFromCookie,
                    workflow_name: getAsyncMethodConfig(selectedMethod)?.workflowName,
                },
            });
            if (status_from_provider === "success" && is_verified) {
                toast.success(__("auth.age_verification.result.message_success.prev_uuid_verification"));
                onAgeVerification(uuidFromCookie, is_verified);
                return true;
            }
        }
        catch (error) {
            removeAgeVerificationUuid();
            return false;
        }
    };
    const triggerVerification = async () => {
        const formValues = form.getValues();
        const verificationMethod = availableMethods.find(({ code }) => code === selectedMethod);
        if (isOptional && !isFormFilled() && verificationMethod?.type !== "async") {
            clearWidgetAvData();
            return onSuccess?.();
        }
        const isValid = await form.trigger();
        if (!isValid) {
            return;
        }
        const isVerificationSkipped = await handleCheckPrevAsyncVerification();
        if (isVerificationSkipped) {
            return;
        }
        if (verificationMethod?.type === "async") {
            try {
                await initAsyncMethod(selectedMethod);
            }
            catch (error) {
                onAgeVerificationFail();
            }
        }
        else {
            await verifyAge(formValues, selectedMethod);
        }
    };
    const tryAgainVerification = () => {
        if (savedAsyncAvData) {
            setIsValidationError(false);
            asyncAgeVerify(savedAsyncAvData);
        }
        else {
            setAsyncPendingPopupOpened(false);
        }
    };
    const returnToAsyncVerificationProcess = () => {
        if (selectedMethod === VERIFF_AV) {
            setAsyncPendingPopupOpened(false);
            triggerVerification();
            return;
        }
        const verificationUrl = savedAsyncAvData?.sessionUrl;
        if (verificationUrl) {
            setIsValidationError(false);
            saveAsyncAvData(savedAsyncAvData);
            onSaveDataToCache?.();
            window.location.href = verificationUrl;
        }
    };
    const cancelAsyncAgeVerificationProcess = () => {
        setAsyncPendingPopupOpened(false);
    };
    const handleSyncAvWidgetVerification = (verificationId) => {
        form.setValue("avpValues.verification_id", verificationId);
        setIsButtonDisabledForWidgetVerification(false);
    };
    if (isAvailableMethodsLoading) {
        return _jsx(Loading, { dataTestId: "ageVerificationAvailableMethodsLoader" });
    }
    return (_jsxs(_Fragment, { children: [availableMethods?.length ? (_jsxs("div", { "data-testid": "ageVerificationMethods", className: styles.formContainer, children: [_jsxs("div", { className: styles.methodsRow, children: [_jsxs("div", { className: styles.methodsContainer, children: [(showImage || availableMethods.length > 1) && (_jsx("div", { className: styles.identificationLabelContainer, children: _jsx(Txt, { variant: "small-text", className: styles.methodsTitle, children: __("auth.age_verification.identification_document_label") }) })), _jsx(MethodSelection, { selectMethod: onSelectMethod, methods: availableMethods, selectedMethod: selectedMethod })] }), showImage && (_jsx("img", { className: styles.verificationImage, src: imagePath, alt: __("auth.age_verification.passport_image_alt") }))] }), method && method.type !== "async" && (_jsx(FieldsArray, { ...method, isOptional: isOptional, fieldName: (name) => `${formPrefix}.${name}` })), _jsx(SyncAvWidgets, { selectedMethod: selectedMethod, onAvFinished: handleSyncAvWidgetVerification }), _jsx(AsyncValidationPopup, { isOpened: isAsyncPendingPopupOpened, onCancel: cancelAsyncAgeVerificationProcess, onTryAgain: returnToAsyncVerificationProcess, onRefresh: tryAgainVerification, isError: isValidationError || isAsyncVerificationFailed }), children, _jsx("div", { className: classNames(styles.actionContainer, buttonClassName), children: _jsx(Button, { isPending: isPending || isVerifing || asyncVerificationPending, disabled: isDisabled || isButtonDisabledForWidgetVerification, color: "primary", "data-testid": "ageVerificationSubmitButton", onClick: () => triggerVerification(), children: buttonLabel || __("profile.age_verification.submit") }) })] })) : (_jsx("p", { children: __("auth.age_verification.no_services_message") })), _jsx("div", { dangerouslySetInnerHTML: {
                    __html: sanitize("<transactionlink-widget></transactionlink-widget>", {
                        ALLOWED_TAGS: ["transactionlink-widget"],
                    }),
                } })] }));
});
//# sourceMappingURL=ValidationMethods.js.map